/*
 *  Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
 *
 *  Licensed under the Apache License, Version 2.0 (the "License").
 *  You may not use this file except in compliance with the License.
 *  A copy of the License is located at
 *
 *  http://aws.amazon.com/apache2.0
 *
 *  or in the "license" file accompanying this file. This file is distributed
 *  on an "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either
 *  express or implied. See the License for the specific language governing
 *  permissions and limitations under the License.
 */

import { toJS } from 'mobx';
import { types, applySnapshot, getEnv } from 'mobx-state-tree';

const KeyValuePair = types.model('KeyValuePair', {
  key: '',
  value: '',
});

// ====================================================================================================================================
// EnvTypeConfig
// ====================================================================================================================================
const EnvTypeConfig = types
  .model('EnvTypeConfig', {
    id: types.identifier,
    name: '',
    desc: '',
    estimatedCostInfo: '',
    allowRoleIds: types.optional(types.array(types.string), []),
    denyRoleIds: types.optional(types.array(types.string), []),
    allowedRegions: types.optional(types.array(types.string), []),

    // The administrative idle-stop timeout applied when a workspace is launched with this
    // configuration, and whether users may change it afterwards. Null means no default applies.
    idleStopMinutes: types.maybeNull(types.number),
    idleStopLocked: types.optional(types.boolean, false),

    params: types.optional(types.array(KeyValuePair), []),
    tags: types.optional(types.array(KeyValuePair), []),
    createdAt: '',
    createdBy: '',
    updatedAt: '',
    updatedBy: '',

    // flag indicating if the env type config is allowed to be used
    // defaulting this to true as the API only returns those env type configs that are usable
    // except for admins when include=all is passed in the query param, it returns all env type configs
    // including the ones the user is not allowed to use when launching an environment
    allowedToUse: types.optional(types.boolean, true),
  })
  .actions((self) => ({
    setEnvTypeConfig(envTypeConfig) {
      applySnapshot(self, envTypeConfig);
    },
  }))
  .views((self) => ({
    get descHtml() {
      const showdown = getEnv(self).showdown;
      return showdown.convert(self.desc);
    },
    get estimatedCostInfoHtml() {
      const raw = self.estimatedCostInfo;
      if (!raw) return '';

      // If it looks like JSON, try to parse and display as a readable list
      if (typeof raw === 'string' && raw.trim().startsWith('{')) {
        try {
          const parsed = JSON.parse(raw);
          if (typeof parsed === 'object' && parsed !== null && !Array.isArray(parsed)) {
            const entries = Object.entries(parsed)
              .map(([region, rate]) => {
                const safeRegion = String(region).replace(/[<>&"'/]/g, '');
                const hourly = parseFloat(rate);
                if (isNaN(hourly)) return `${safeRegion}: N/A`;
                const daily = Math.round(hourly * 24 * 100) / 100;
                return `${safeRegion}: ${hourly.toFixed(2)} USD/hr (${daily.toFixed(2)}/day)`;
              })
              .join(', ');
            return entries;
          }
        } catch (e) {
          // Not valid JSON, fall through
        }
      }

      // Legacy plain text: sanitize to prevent XSS, then render via showdown
      const sanitized = String(raw).replace(/<script[\s\S]*?<\/script>/gi, '').replace(/on\w+\s*=/gi, '');
      const showdown = getEnv(self).showdown;
      return showdown.convert(sanitized);
    },
    get instanceType() {
      const params = self.params;
      const paramsAsJson = toJS(params);
      const instanceTypeIndex = paramsAsJson.findIndex(({ key }) => {
        return key === 'InstanceType' || key === 'MasterInstanceType';
      });
      if (instanceTypeIndex < 0) {
        return 'Not available';
      }
      return paramsAsJson[instanceTypeIndex].value;
    },
  }));

export default EnvTypeConfig;
export { EnvTypeConfig };
